import React from 'react';

/**
 * Renders a context_json object in a human-readable way
 * instead of raw JSON.
 */
export default function ContextViewer({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="context-viewer">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="context-section">
          <h4>{formatKey(key)}</h4>
          {renderValue(value)}
        </div>
      ))}
    </div>
  );
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderValue(value: unknown): React.JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-muted">—</span>;
  }

  if (typeof value === 'string') {
    return <p>{value}</p>;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <p>{String(value)}</p>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted">None</span>;

    // Array of strings
    if (typeof value[0] === 'string') {
      return (
        <ul className="context-list">
          {value.map((item, i) => <li key={i}>{String(item)}</li>)}
        </ul>
      );
    }

    // Array of objects (e.g. potential_parents)
    if (typeof value[0] === 'object') {
      return (
        <div className="context-objects">
          {value.map((item, i) => (
            <div key={i} className="context-object-item">
              {Object.entries(item as Record<string, unknown>).map(([k, v]) => (
                <span key={k}><strong>{formatKey(k)}:</strong> {String(v)} </span>
              ))}
            </div>
          ))}
        </div>
      );
    }
  }

  if (typeof value === 'object') {
    return (
      <dl className="context-dl">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k}>
            <dt>{formatKey(k)}</dt>
            <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return <pre>{JSON.stringify(value, null, 2)}</pre>;
}
